import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from '@/lib/toast'
import { api } from '@/lib/api'
import type { ContactUpdateListResponse } from '@/lib/types'

export function useContactUpdates(options?: {
  contactId?: string
  unreadOnly?: boolean
  page?: number
  limit?: number
}) {
  const params = new URLSearchParams()
  if (options?.contactId) params.set('contactId', options.contactId)
  if (options?.unreadOnly) params.set('unread', 'true')
  if (options?.page) params.set('page', String(options.page))
  if (options?.limit) params.set('limit', String(options.limit))

  const qs = params.toString()
  return useQuery({
    queryKey: ['contact-updates', options],
    queryFn: () => api<ContactUpdateListResponse>(`/api/contact-updates${qs ? `?${qs}` : ''}`),
  })
}

export function useUnreadUpdatesCount() {
  return useQuery({
    queryKey: ['contact-updates-unread-count'],
    queryFn: () => api<{ count: number }>('/api/contact-updates/unread-count'),
    refetchInterval: 60_000, // refresh every minute
  })
}

export function useMarkUpdateRead() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (updateId: string) =>
      api<{ success: boolean }>(`/api/contact-updates/${updateId}/read`, { method: 'PATCH' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['contact-updates'] })
      qc.invalidateQueries({ queryKey: ['contact-updates-unread-count'] })
      qc.invalidateQueries({ queryKey: ['dashboard'] })
    },
  })
}

export function useMarkAllUpdatesRead() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () =>
      api<{ success: boolean }>('/api/contact-updates/mark-all-read', { method: 'POST' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['contact-updates'] })
      qc.invalidateQueries({ queryKey: ['contact-updates-unread-count'] })
      qc.invalidateQueries({ queryKey: ['dashboard'] })
      toast.success('All updates marked as read')
    },
  })
}
